import {
  and,
  asc,
  count,
  desc,
  eq,
  gte,
  inArray,
  isNotNull,
  isNull,
  lte,
  notInArray,
  or,
} from 'drizzle-orm';
import { categories, history, ledgerEntries, tiles, userSettings } from '@tahi/db';
import {
  singletonId,
  sortCategories,
  type CategoryLike,
  type DateRange,
  type LedgerEntryLike,
  type LedgerKind,
  type NoteHistoryItem,
  type WeekStart,
} from '@tahi/core';
import type { LocalDb } from '../localDb';

export interface SpendingEntry extends LedgerEntryLike {
  currency: string;
  fundId: string | null;
  /** v0.5 Accounts. */
  accountId: string | null;
  toAccountId: string | null;
  sourceId: string | null;
  recordedTz: string;
  createdAt: string;
}

export interface SpendingCategory extends CategoryLike {
  icon: string | null;
}

export interface EntryHistoryRow {
  id: string;
  action: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  sourceModule: string | null;
  at: string;
}

type LedgerRow = typeof ledgerEntries.$inferSelect;
type CategoryRow = typeof categories.$inferSelect;

function toEntry(r: LedgerRow): SpendingEntry {
  return {
    id: r.id,
    date: r.date,
    amountMinor: r.amountMinor,
    kind: r.kind as LedgerKind,
    categoryId: r.categoryId ?? null,
    method: r.method ?? null,
    sourceModule: r.sourceModule,
    note: r.note ?? null,
    deletedAt: r.deletedAt ?? null,
    currency: r.currency,
    fundId: r.fundId ?? null,
    accountId: r.accountId ?? null,
    toAccountId: r.toAccountId ?? null,
    sourceId: r.sourceId ?? null,
    recordedTz: r.recordedTz,
    createdAt: r.createdAt,
  };
}

function toCategory(r: CategoryRow): SpendingCategory {
  return {
    id: r.id,
    key: r.key ?? null,
    name: r.name,
    icon: r.icon ?? null,
    hidden: Boolean(r.hidden),
    position: r.position ?? 0,
    deletedAt: r.deletedAt ?? null,
  };
}

function parseJson(v: unknown): Record<string, unknown> | null {
  if (v == null) return null;
  if (typeof v === 'string') {
    try {
      return JSON.parse(v) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  return v as Record<string, unknown>;
}

function liveCategoryQuery(db: LocalDb, userId: string) {
  return db
    .select({ id: categories.id })
    .from(categories)
    .where(and(eq(categories.userId, userId), isNull(categories.deletedAt)));
}

async function liveCategoryIds(db: LocalDb, userId: string): Promise<Set<string>> {
  return new Set((await liveCategoryQuery(db, userId)).map((r) => r.id));
}

/**
 * A deleted category is soft-deleted and its entries keep their categoryId (so Undo, and a restore
 * synced from another device, bring everything back). Until then they read as uncategorized.
 */
function readCategory(e: SpendingEntry, live: ReadonlySet<string>): SpendingEntry {
  return e.categoryId !== null && !live.has(e.categoryId) ? { ...e, categoryId: null } : e;
}

/** All live ledger rows in the range (any kind); core decides what counts. Newest first. */
export async function listEntriesInRange(
  db: LocalDb,
  userId: string,
  range: DateRange,
): Promise<SpendingEntry[]> {
  const rows = await db
    .select()
    .from(ledgerEntries)
    .where(
      and(
        eq(ledgerEntries.userId, userId),
        isNull(ledgerEntries.deletedAt),
        gte(ledgerEntries.date, range.start),
        lte(ledgerEntries.date, range.end),
      ),
    )
    .orderBy(desc(ledgerEntries.date), desc(ledgerEntries.createdAt));
  const live = await liveCategoryIds(db, userId);
  return rows.map((r) => readCategory(toEntry(r), live));
}

/** One entry, including a soft-deleted one (so its detail page can offer Restore). */
export async function getEntry(
  db: LocalDb,
  userId: string,
  id: string,
): Promise<SpendingEntry | null> {
  const rows = await db
    .select()
    .from(ledgerEntries)
    .where(and(eq(ledgerEntries.userId, userId), eq(ledgerEntries.id, id)))
    .limit(1);
  if (!rows[0]) return null;
  return readCategory(toEntry(rows[0]), await liveCategoryIds(db, userId));
}

/** Every category id any entry points at, deleted entries included (v0.1.1 cleanup). */
export async function listUsedCategoryIds(db: LocalDb, userId: string): Promise<Set<string>> {
  const rows = await db
    .selectDistinct({ id: ledgerEntries.categoryId })
    .from(ledgerEntries)
    .where(and(eq(ledgerEntries.userId, userId), isNotNull(ledgerEntries.categoryId)));
  return new Set(rows.map((r) => r.id).filter((id): id is string => id !== null));
}

/** Categories including soft-deleted ones (cleanup and restore checks). */
export async function listAllCategories(db: LocalDb, userId: string): Promise<SpendingCategory[]> {
  const rows = await db.select().from(categories).where(eq(categories.userId, userId));
  return rows.map(toCategory);
}

export async function listCategories(db: LocalDb, userId: string): Promise<SpendingCategory[]> {
  const rows = await db
    .select()
    .from(categories)
    .where(and(eq(categories.userId, userId), isNull(categories.deletedAt)));
  return sortCategories(rows.map(toCategory));
}

/** Keys of every default category this user has ever had (deleted included), for idempotent seeding. */
export async function listCategoryKeys(db: LocalDb, userId: string): Promise<Set<string>> {
  const rows = await db
    .select({ key: categories.key })
    .from(categories)
    .where(and(eq(categories.userId, userId), isNotNull(categories.key)));
  return new Set(rows.map((r) => r.key).filter((k): k is string => k !== null));
}

/** Recent categorized expenses with notes, for category suggestions. */
export async function listNoteHistory(
  db: LocalDb,
  userId: string,
  limit = 500,
): Promise<NoteHistoryItem[]> {
  const rows = await db
    .select({
      note: ledgerEntries.note,
      categoryId: ledgerEntries.categoryId,
      date: ledgerEntries.date,
    })
    .from(ledgerEntries)
    .where(
      and(
        eq(ledgerEntries.userId, userId),
        eq(ledgerEntries.kind, 'expense'),
        isNull(ledgerEntries.deletedAt),
        isNotNull(ledgerEntries.note),
        isNotNull(ledgerEntries.categoryId),
      ),
    )
    .orderBy(desc(ledgerEntries.date))
    .limit(limit);
  return rows.map((r) => ({
    note: r.note ?? null,
    categoryId: r.categoryId ?? null,
    date: r.date,
  }));
}

/** Expenses with no category, or whose category was deleted. */
export async function listUncategorized(db: LocalDb, userId: string): Promise<SpendingEntry[]> {
  const rows = await db
    .select()
    .from(ledgerEntries)
    .where(
      and(
        eq(ledgerEntries.userId, userId),
        eq(ledgerEntries.kind, 'expense'),
        isNull(ledgerEntries.deletedAt),
        or(
          isNull(ledgerEntries.categoryId),
          notInArray(ledgerEntries.categoryId, liveCategoryQuery(db, userId)),
        ),
      ),
    )
    .orderBy(desc(ledgerEntries.date), desc(ledgerEntries.createdAt));
  return rows.map((r) => ({ ...toEntry(r), categoryId: null }));
}

export async function countSpendingEntries(db: LocalDb, userId: string): Promise<number> {
  const rows = await db
    .select({ n: count() })
    .from(ledgerEntries)
    .where(
      and(
        eq(ledgerEntries.userId, userId),
        isNull(ledgerEntries.deletedAt),
        inArray(ledgerEntries.kind, ['expense', 'refund']),
      ),
    );
  return rows[0]?.n ?? 0;
}

export async function listEntryHistory(
  db: LocalDb,
  userId: string,
  id: string,
): Promise<EntryHistoryRow[]> {
  const rows = await db
    .select()
    .from(history)
    .where(
      and(
        eq(history.userId, userId),
        eq(history.entityType, 'ledger_entries'),
        eq(history.entityId, id),
      ),
    )
    .orderBy(asc(history.at));
  return rows.map((r) => ({
    id: r.id,
    action: r.action,
    before: parseJson(r.before),
    after: parseJson(r.after),
    sourceModule: r.sourceModule ?? null,
    at: r.at,
  }));
}

export interface UserSettings {
  weekStart: WeekStart;
  seeds: Record<string, string>;
}

/** The user's settings row, or null if it doesn't exist yet (defaults apply). */
export async function getUserSettings(db: LocalDb, userId: string): Promise<UserSettings | null> {
  const rows = await db
    .select()
    .from(userSettings)
    .where(eq(userSettings.id, singletonId(userId, 'user_settings')))
    .limit(1);
  const r = rows[0];
  if (!r || r.deletedAt) return null;
  return { weekStart: r.weekStart === 0 ? 0 : 1, seeds: r.seeds ?? {} };
}

export async function listTileTypes(
  db: LocalDb,
  userId: string,
  dashboardId: string,
): Promise<string[]> {
  const rows = await db
    .select({ type: tiles.type })
    .from(tiles)
    .where(and(eq(tiles.userId, userId), eq(tiles.dashboardId, dashboardId)));
  return rows.map((r) => r.type);
}
